import type { CityConfig } from "@/pages/city-template/types";

export const rutlandConfig: CityConfig = {
  city: "Rutland",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "rutland",
  route: "/cities/rutland",
  title: "Expedited Passport Services in Rutland, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Rutland, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Rutland, Killington, Ludlow, Brandon, and all of Southern Vermont.",
  h1: "Expedited Passport Services in Rutland, VT",
  heroSubtitle:
    "Rutland travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the New York Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20view%20of%20Rutland%20Vermont%20downtown%20at%20golden%20hour%20with%20historic%20marble%20buildings%20and%20charming%20Main%20Street%2C%20green%20mountain%20foothills%20in%20the%20background%2C%20warm%20amber%20light%2C%20tree-lined%20streets%20with%20lush%20foliage%2C%20classic%20New%20England%20small%20cityscape%2C%20scenic%20Vermont%20countryside%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-rut-hero-001&orientation=landscape",
  heroImageAlt: "Rutland Vermont downtown with historic marble buildings and Green Mountains",
  geoPosition: "43.6106; -72.9726",
  geoPlacename: "Rutland, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~200 miles from Rutland via I-87",
  },
  nearbyCitiesZips: [
    { name: "Rutland", zips: "05701, 05702" },
    { name: "Rutland Town", zips: "05701" },
    { name: "Killington", zips: "05751" },
    { name: "Ludlow", zips: "05149" },
    { name: "Brandon", zips: "05733" },
    { name: "Pittsford", zips: "05763" },
    { name: "Proctor", zips: "05765" },
    { name: "Fair Haven", zips: "05743" },
    { name: "Castleton", zips: "05735" },
    { name: "West Rutland", zips: "05777" },
    { name: "Mendon", zips: "05701" },
    { name: "Wallingford", zips: "05773" },
    { name: "Mount Holly", zips: "05758" },
    { name: "Poultney", zips: "05764" },
    { name: "Wells", zips: "05774" },
    { name: "Middlebury", zips: "05753" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Stowe", zips: "05672" },
    { name: "Brattleboro", zips: "05301, 05302, 05303" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Rutland travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Southern Vermont",
      desc: "Rutland, Killington, Ludlow, Brandon, and all surrounding Southern Vermont communities.",
    },
  ],
  testimonials: [
    {
      name: "Laura P.",
      location: "Rutland, VT",
      rating: 5,
      text: "I needed my passport renewed for a ski trip to Austria in 5 days. Fast Passport Center had it back to me in Rutland in 3 days. Their Southern Vermont service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Tom H.",
      location: "Killington, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Killington and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Karen S.",
      location: "Ludlow, VT",
      rating: 5,
      text: "My daughter needed a passport for a school trip to Quebec. Fast Passport Center handled the child passport process and had it ready in 4 days. Ludlow families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Mark D.",
      location: "Brandon, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Europe. Fast Passport Center replaced it in 3 days and saved my trip. Brandon residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "rut-1",
      name: "Rutland Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "151 West Street",
      city: "Rutland",
      state: "VT",
      zip: "05701",
      phone: "802-775-4016",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Rutland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "rut-2",
      name: "Killington Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "3745 Route 100",
      city: "Killington",
      state: "VT",
      zip: "05751",
      phone: "802-422-9934",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Killington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "rut-3",
      name: "Ludlow Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "164 Main Street",
      city: "Ludlow",
      state: "VT",
      zip: "05149",
      phone: "802-228-8826",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Ludlow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "rut-4",
      name: "Brandon Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2 Park Street",
      city: "Brandon",
      state: "VT",
      zip: "05733",
      phone: "802-247-6601",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Brandon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "rut-5",
      name: "Fair Haven Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "3 S. Main Street",
      city: "Fair Haven",
      state: "VT",
      zip: "05743",
      phone: "802-265-3666",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Fair Haven",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "rut-6",
      name: "Rutland City Clerk",
      type: "City Clerk / Passport Services",
      address: "52 Washington Street",
      city: "Rutland",
      state: "VT",
      zip: "05701",
      phone: "802-773-1800",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Rutland City Hall",
      website: "https://www.rutlandcity.com/",
      websiteLabel: "City of Rutland",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Rutland, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Rutland?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Rutland?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Rutland, Killington, and Ludlow. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Rutland?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Rutland?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Rutland?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Rutland. We serve Rutland and all of Southern Vermont through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Rutland for international travel?",
      a: "Rutland-Southern Vermont Regional Airport (RUT) is the closest at ~5 miles. Albany International (ALB) is ~75 miles, Burlington International (BTV) is ~65 miles, and Boston Logan (BOS) is ~170 miles. Most Rutland residents use Albany or Burlington for international travel.",
    },
    {
      q: "How much does expedited passport service cost in Rutland?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Rutland?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Rutland?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Rutland?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Rutland have a local passport agency?",
      a: "No, Rutland does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 200 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Rutland", address: "various CVS locations in Rutland", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Rutland", address: "various Walgreens locations in Rutland", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Rutland", address: "various UPS Store locations in Rutland", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Rutland", address: "various FedEx Office locations in Rutland", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Rutland", address: "various Walmart locations in Rutland", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Rutland",
      address: "92 State Street, Rutland, VT 05701",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: true,
    },
    {
      name: "VT DMV — Montpelier",
      address: "118 State Street, Montpelier, VT 05602",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Vermont DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RUT",
      name: "Rutland-Southern Vermont Regional Airport",
      distance: "~5 miles from Rutland",
      address: "1002 Airport Rd, North Clarendon, VT 05759",
      note: "Closest to Rutland with seasonal Cape Air service to Boston. Limited commercial flights.",
      flightUrl: "https://flyrutlandvt.com/",
      flightLabel: "Rutland Regional",
      featured: true,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~75 miles from Rutland",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Major alternative for Rutland travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~65 miles from Rutland",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport with more flight options. Common for Rutland residents.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Rutland travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Rutland residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Rutland residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Rutland residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Rutland, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Rutland residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Rutland travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Rutland residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Rutland residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Rutland residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Rutland." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Rutland." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Rutland travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Rutland travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Rutland team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Rutland address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Rutland and all of Southern Vermont as a remote courier service. We do not have a physical office in Rutland, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Rutland, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Rutland neighborhoods and surrounding Southern Vermont communities. From downtown to the outskirts, from the marble quarries to the ski resorts — we serve every Rutland community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Rutland and all surrounding Southern Vermont communities. Whether you're in downtown Rutland or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Rutland customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Rutland travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Rutland and Southern Vermont travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Rutland, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
};

export default rutlandConfig;